/**
 * Feature Flag Constants
 * 
 * Default feature flag keys and static default values.
 * These are used as fallbacks when API is unavailable or flag doesn't exist.
 */

export const FEATURE_FLAG_KEYS = {
  // UI Features
  NEW_DASHBOARD: 'NEW_DASHBOARD',
  ENHANCED_SCHEDULE: 'ENHANCED_SCHEDULE',
  DARK_MODE: 'DARK_MODE',
  SHOW_SOCIAL_LINKS: 'SHOW_SOCIAL_LINKS',
  
  // Functionality
  ADVANCED_ANALYTICS: 'ADVANCED_ANALYTICS',
  TEAM_COLLABORATION: 'TEAM_COLLABORATION',
  CUSTOM_INTEGRATIONS: 'CUSTOM_INTEGRATIONS',
  
  // Notifications
  PUSH_NOTIFICATIONS: 'PUSH_NOTIFICATIONS',
  EMAIL_NOTIFICATIONS: 'EMAIL_NOTIFICATIONS',
  SMS_NOTIFICATIONS: 'SMS_NOTIFICATIONS',
  
  // Beta Features
  BETA_FEATURES: 'BETA_FEATURES',
  EXPERIMENTAL_UI: 'EXPERIMENTAL_UI',
  EXP_LANDING: 'EXP_LANDING',
  AI_AVATARS: 'AI_AVATARS',
  POST_SUBSCRIPTION_ONBOARDING: 'POST_SUBSCRIPTION_ONBOARDING',
  
  // Mobile Features
  MOBILE_APP: 'MOBILE_APP',
  OFFLINE_MODE: 'OFFLINE_MODE',
  
  // Payment Features
  STRIPE_CONNECT: 'STRIPE_CONNECT',
  MULTI_CURRENCY: 'MULTI_CURRENCY',
  GB_MARKET_EXPANSION: 'GB_MARKET_EXPANSION',

  /** Landing directory: public academy/club directory nav and promotion (off until ready). */
  LANDING_DIRECTORY: 'LANDING_DIRECTORY',
} as const;

export type FeatureFlagKey = typeof FEATURE_FLAG_KEYS[keyof typeof FEATURE_FLAG_KEYS];

/**
 * Static default values for feature flags
 * These are used as fallbacks when API is unavailable or flag doesn't exist
 */
export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlagKey, boolean> = {
  // UI Features
  [FEATURE_FLAG_KEYS.NEW_DASHBOARD]: false,
  [FEATURE_FLAG_KEYS.ENHANCED_SCHEDULE]: false,
  [FEATURE_FLAG_KEYS.DARK_MODE]: true,
  [FEATURE_FLAG_KEYS.SHOW_SOCIAL_LINKS]: false,
  
  // Functionality
  [FEATURE_FLAG_KEYS.ADVANCED_ANALYTICS]: true,
  [FEATURE_FLAG_KEYS.TEAM_COLLABORATION]: true,
  [FEATURE_FLAG_KEYS.CUSTOM_INTEGRATIONS]: true,
  
  // Notifications
  [FEATURE_FLAG_KEYS.PUSH_NOTIFICATIONS]: process.env.NODE_ENV === 'development',
  [FEATURE_FLAG_KEYS.EMAIL_NOTIFICATIONS]: true,
  [FEATURE_FLAG_KEYS.SMS_NOTIFICATIONS]: false,
  
  // Beta Features
  [FEATURE_FLAG_KEYS.BETA_FEATURES]: process.env.NODE_ENV === 'development',
  [FEATURE_FLAG_KEYS.EXPERIMENTAL_UI]: false,
  [FEATURE_FLAG_KEYS.EXP_LANDING]: true,
  [FEATURE_FLAG_KEYS.AI_AVATARS]: process.env.NODE_ENV === 'development',
  [FEATURE_FLAG_KEYS.POST_SUBSCRIPTION_ONBOARDING]: false,
  
  // Mobile Features
  [FEATURE_FLAG_KEYS.MOBILE_APP]: true,
  [FEATURE_FLAG_KEYS.OFFLINE_MODE]: false,
  
  // Payment Features
  [FEATURE_FLAG_KEYS.STRIPE_CONNECT]: true,
  [FEATURE_FLAG_KEYS.MULTI_CURRENCY]: true,
  [FEATURE_FLAG_KEYS.GB_MARKET_EXPANSION]: false,
  [FEATURE_FLAG_KEYS.LANDING_DIRECTORY]: false,
};

/**
 * Feature flag metadata (descriptions, categories)
 */
export const FEATURE_FLAG_METADATA: Record<
  FeatureFlagKey,
  { description: string; category: string }
> = {
  // UI Features
  [FEATURE_FLAG_KEYS.NEW_DASHBOARD]: {
    description: 'Enable the new dashboard interface',
    category: 'UI',
  },
  [FEATURE_FLAG_KEYS.ENHANCED_SCHEDULE]: {
    description: 'Enable enhanced schedule view with additional features',
    category: 'UI',
  },
  [FEATURE_FLAG_KEYS.DARK_MODE]: {
    description: 'Enable dark mode theme',
    category: 'UI',
  },
  [FEATURE_FLAG_KEYS.SHOW_SOCIAL_LINKS]: {
    description: 'Show social media icons and links in the UI',
    category: 'UI',
  },
  
  // Functionality
  [FEATURE_FLAG_KEYS.ADVANCED_ANALYTICS]: {
    description: 'Enable advanced analytics features',
    category: 'Functionality',
  },
  [FEATURE_FLAG_KEYS.TEAM_COLLABORATION]: {
    description: 'Enable team collaboration features',
    category: 'Functionality',
  },
  [FEATURE_FLAG_KEYS.CUSTOM_INTEGRATIONS]: {
    description: 'Enable custom integrations',
    category: 'Functionality',
  },
  
  // Notifications
  [FEATURE_FLAG_KEYS.PUSH_NOTIFICATIONS]: {
    description: 'Enable push notifications',
    category: 'Notifications',
  },
  [FEATURE_FLAG_KEYS.EMAIL_NOTIFICATIONS]: {
    description: 'Enable email notifications',
    category: 'Notifications',
  },
  [FEATURE_FLAG_KEYS.SMS_NOTIFICATIONS]: {
    description: 'Enable SMS notifications',
    category: 'Notifications',
  },
  
  // Beta Features
  [FEATURE_FLAG_KEYS.BETA_FEATURES]: {
    description: 'Enable beta features',
    category: 'Beta',
  },
  [FEATURE_FLAG_KEYS.EXPERIMENTAL_UI]: {
    description: 'Enable experimental UI features',
    category: 'Beta',
  },
  [FEATURE_FLAG_KEYS.EXP_LANDING]: {
    description: 'Enable experimental landing page features',
    category: 'Beta',
  },
  [FEATURE_FLAG_KEYS.AI_AVATARS]: {
    description: 'Enable AI-generated tennis-themed avatars (future feature)',
    category: 'Beta',
  },
  [FEATURE_FLAG_KEYS.POST_SUBSCRIPTION_ONBOARDING]: {
    description: 'Show the post-subscription academy activation wizard after checkout',
    category: 'Beta',
  },
  
  // Mobile Features
  [FEATURE_FLAG_KEYS.MOBILE_APP]: {
    description: 'Enable mobile app features',
    category: 'Mobile',
  },
  [FEATURE_FLAG_KEYS.OFFLINE_MODE]: {
    description: 'Enable offline mode in mobile app',
    category: 'Mobile',
  },
  
  // Payment Features
  [FEATURE_FLAG_KEYS.STRIPE_CONNECT]: {
    description: 'Enable Stripe Connect integration',
    category: 'Payment',
  },
  [FEATURE_FLAG_KEYS.MULTI_CURRENCY]: {
    description: 'Enable multi-currency support',
    category: 'Payment',
  },
  [FEATURE_FLAG_KEYS.GB_MARKET_EXPANSION]: {
    description: 'Enable feature-flagged United Kingdom / GBP market support',
    category: 'Payment',
  },
  [FEATURE_FLAG_KEYS.LANDING_DIRECTORY]: {
    description: 'Show the public academy and club directory link on the landing site',
    category: 'UI',
  },
};

